import React from 'react'

import { observer } from 'mobx-react'

import {
  ASSIGNED_STAR_CENTER,
  ASSIGNED_STAR_SPEC,
  MAP_SHADOW_FILTER,
  MARKER_CLEARANCE_PX,
  PIN_PATH_DATA,
  PIN_SPEC,
} from '~/client/src/shared/map/markerGeometry'
import ThemeMode from '~/client/src/shared/utils/ThemeModeManager'

import Colors from '~/client/src/shared/theme.module.scss'

const GLYPH_CLASSES = 'tw:absolute tw:left-1/2 tw:-translate-x-1/2'
const DEFAULT_GLYPH_COLOR = '#ffffff'

const SIZE_PX = {
  md: PIN_SPEC.boxPx,
  lg: PIN_SPEC.largeBoxPx,
} as const

interface IProps {
  // Rendered with the geometry the pin owns.
  glyph?: (opts: { size: number; color: string }) => React.ReactNode
  size?: keyof typeof SIZE_PX
  // Defaults to the dark pin. Callers paint their own state — primary while a pin is being
  // moved, primary for the project address pin.
  fill?: string
  stroke?: string
  // Hangs off a point instead of taking part in its parent's layout. A plain marker child
  // stays in flow, or the marker's anchor has no box to measure.
  overlay?: boolean
  // Overlay only: rides a location marker, so it lifts clear of it.
  liftsAboveMarker?: boolean
  // Ink for the glyph. White suits every dark pin today.
  glyphColor?: string
  // Assigned to the viewer: adds the star, exactly as the GL raster bakes one.
  isAssigned?: boolean
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
}

// The one DOM pin, for things being placed rather than displayed — every stateful pin is a GL
// symbol now. Domain-agnostic: it owns the silhouette, size and positioning; the caller draws the
// glyph. inset-x-0 is load-bearing for an overlay: without it the absolute box shrink-wraps and
// sits half a pin right of its wrapper's axis. globe-location is kept as the hook MapBoxViewer uses
// to switch pointer events off.
const DomPin = observer(
  ({
    glyph,
    size = 'md',
    overlay,
    fill,
    stroke,
    liftsAboveMarker,
    glyphColor = DEFAULT_GLYPH_COLOR,
    isAssigned,
    className = '',
    onClick,
  }: IProps) => {
    const boxPx = SIZE_PX[size]
    const glyphPx = Math.round(boxPx * PIN_SPEC.glyphRatio)
    // Centred on the head circle.
    const glyphTopPx = boxPx * PIN_SPEC.headCenterRatio - glyphPx / 2
    // The star rides the pin's viewBox for its ANCHOR, but a viewBox unit is boxPx/49 css
    // pixels, so left alone it would grow with the pin — 19.6px on the 60px thumbnail pin
    // against Figma's 16. Counter-scaled about its own centre it holds that 16 at any size.
    const starScale = PIN_SPEC.viewBoxHeight / boxPx
    const starHalf = (ASSIGNED_STAR_SPEC.sizePx / 2) * starScale

    return (
      <div
        className={`globe-location tw:z-[1] tw:flex tw:flex-col tw:items-center tw:justify-center tw:hover:z-[2] ${
          overlay ? 'tw:absolute tw:inset-x-0' : ''
        } ${className}`}
        style={
          overlay
            ? { bottom: liftsAboveMarker ? MARKER_CLEARANCE_PX : 0 }
            : undefined
        }
      >
        {/* Only a pin that handles its own click swallows the gesture — a click there must not
            also reach the map. A pin without one stays transparent, so its Marker can still take
            the mousedown that starts a drag (the project address pin is dragged, never clicked). */}
        <div
          // relative so the glyph and the star measure from the silhouette rather than from
          // whichever ancestor happens to be positioned. The shadow hangs here rather than on
          // the pin alone, so pin, glyph and star cast one between them.
          className={`tw:relative tw:flex tw:items-center tw:justify-center ${
            onClick ? 'tw:cursor-pointer' : ''
          }`}
          style={{ filter: MAP_SHADOW_FILTER }}
          onMouseDown={onClick && (e => e.stopPropagation())}
          onClick={
            onClick &&
            (e => {
              e.stopPropagation()
              e.preventDefault()
              onClick(e)
            })
          }
        >
          <svg
            // ! beats the inline base size; literal because Tailwind extracts statically.
            className="tw:transition-all tw:duration-150 tw:group-hover/pin:size-[50px]! tw:group-hover/pin-move:size-[50px]!"
            style={{ width: boxPx, height: boxPx, overflow: 'visible' }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={PIN_SPEC.viewBox}
          >
            <g
              fill={fill ?? ThemeMode.getHEXColor(Colors.neutral0)}
              stroke={stroke ?? ThemeMode.getHEXColor(Colors.neutral100)}
              strokeWidth={`${PIN_SPEC.strokePx}px`}
              fillRule="evenodd"
            >
              <path d={PIN_PATH_DATA} />
            </g>
          </svg>
          {!!glyph && (
            <span
              className={GLYPH_CLASSES}
              style={{
                width: glyphPx,
                height: glyphPx,
                top: Math.round(glyphTopPx),
              }}
            >
              {glyph({ size: glyphPx, color: glyphColor })}
            </span>
          )}
          {/* Its own layer, over the glyph, as in Figma — and on the pin's viewBox, so the
              star's coordinates are the spec's own numbers and it grows with the pin. */}
          {isAssigned && (
            <svg
              className="tw:pointer-events-none tw:absolute tw:left-1/2 tw:top-1/2"
              // Sized and centred explicitly, NOT inset-0: an svg is a replaced element with
              // an intrinsic ratio from its viewBox, so inset-0 with auto sizes resolves the
              // height off the width and the ratio (48 -> 48x67.2) and the star came out
              // 49/35 too big. Centring matches the flex-centred pin whatever the wrapper.
              style={{
                width: boxPx,
                height: boxPx,
                overflow: 'visible',
                transform: 'translate(-50%, -50%)',
              }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox={PIN_SPEC.viewBox}
              aria-hidden="true"
            >
              <path
                d={ASSIGNED_STAR_SPEC.path}
                transform={`translate(${ASSIGNED_STAR_CENTER.x - starHalf} ${
                  ASSIGNED_STAR_CENTER.y - starHalf
                }) scale(${starScale})`}
                fill="var(--status-assigned-star)"
                stroke={Colors.mapWhite}
                strokeWidth={ASSIGNED_STAR_SPEC.strokePx}
              />
            </svg>
          )}
        </div>
      </div>
    )
  },
)

export default DomPin
